import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuditDetailDrawer } from './audit-detail-drawer.jsx';
import { PLANS, addDays, fmtDate, makeDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { USE_MOCK_TENANTS, saShouldUseMockNotifications } from './mvp-config.jsx';
import { PlanEditorDrawer } from './plan-editor-drawer.jsx';
import { ConfirmModal, StatusBadge } from './shell.jsx';
import { UserEditorDrawer } from './user-editor-drawer.jsx';
import { paths } from '../src/router/app-paths.js';
// Páginas secundárias: Planos & Assinaturas, Usuários internos, Configurações, Auditoria

// ── PLANOS & ASSINATURAS ─────────────────────────────────────────────────────
const PlanosPage = ({ onToast, stores }) => {
  const [plans, setPlans] = useState(() =>
    PLANS.map(p => ({ ...p, active: true, highlight: p.id === 'pro', description: '', trialDays: 14, yearlyDiscount: 20 }))
  );
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const openCreate = () => { setEditingPlan(null); setDrawerOpen(true); };
  const openEdit   = (p) => { setEditingPlan(p);   setDrawerOpen(true); };

  const handleSave = (saved) => {
    setPlans(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
    setDrawerOpen(false);
    if (onToast) onToast(editingPlan ? `Plano "${saved.name}" atualizado` : `Plano "${saved.name}" criado`);
  };

  const handleDelete = (planId) => {
    const inUse = stores.filter(s => s.plan.id === planId).length;
    if (inUse > 0) { onToast(`Não é possível excluir: ${inUse} loja(s) neste plano`); return; }
    setPlans(prev => prev.filter(p => p.id !== planId));
    if (onToast) onToast('Plano removido');
  };

  const fmtBRL = (v) => 'R$ ' + Math.round(v).toLocaleString('pt-BR');

  return (
    <div className="content">
      <div className="page-header">
        <div>
          <h1>Planos & Assinaturas</h1>
          <p className="sub">Estrutura de preços e distribuição entre as lojas da plataforma.</p>
        </div>
        <div className="actions">
          <button className="btn primary" onClick={openCreate}><Icon name="plus" size={14} />Novo plano</button>
        </div>
      </div>

      {/* Cards de plano */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
        {plans.map(p => {
          const count = stores.filter(s => s.plan.id === p.id).length;
          const mrr   = stores.filter(s => s.plan.id === p.id).reduce((a, b) => a + b.mrr, 0);
          const pct   = stores.length > 0 ? Math.round(count / stores.length * 100) : 0;
          return (
            <div key={p.id} className="card" style={{ padding: 18, position: 'relative', opacity: p.active ? 1 : 0.55, outline: p.highlight ? '2px solid var(--brand-navy)' : 'none', outlineOffset: 2 }}>
              {p.highlight && (
                <div style={{ position: 'absolute', top: -1, right: 14, background: 'var(--brand-navy)', color: 'white', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '0 0 6px 6px' }}>Recomendado</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', fontWeight: 600 }}>{p.name}</div>
                <div style={{ display: 'flex', gap: 2 }}>
                  <button className="icon-action" title="Editar plano" onClick={() => openEdit(p)}><Icon name="edit" size={13} /></button>
                  <button className="icon-action danger" title="Excluir plano" onClick={() => handleDelete(p.id)}><Icon name="x" size={13} /></button>
                </div>
              </div>
              {!p.active && <div style={{ marginBottom: 6 }}><span className="badge neutral"><span className="dot"></span>Inativo</span></div>}
              <div style={{ fontSize: 26, fontWeight: 600, margin: '6px 0 2px', letterSpacing: '-0.02em' }}>{fmtBRL(p.price)}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>/mês</span></div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>{count} loja{count !== 1 ? 's' : ''} · {pct}% da base · {fmtBRL(mrr)} MRR</div>
              {p.description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, fontStyle: 'italic' }}>{p.description}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Pageviews/mês</span><span style={{ fontWeight: 500 }}>{p.pageviews.toLocaleString('pt-BR')}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Usuários</span><span style={{ fontWeight: 500 }}>{p.users}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)' }}>Trial</span><span style={{ fontWeight: 500 }}>{p.trialDays > 0 ? `${p.trialDays} dias` : 'Sem trial'}</span></div>
              </div>
            </div>
          );
        })}
        <div className="card" style={{ padding: 18, border: '1.5px dashed var(--border-strong)', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-muted)', minHeight: 200 }} onClick={openCreate}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={18} /></div>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Novo plano</span>
        </div>
      </div>

      {/* Tabela de assinaturas */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Assinaturas ativas</h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{stores.length} assinaturas</span>
        </div>
        <table className="data">
          <thead><tr><th>Loja</th><th>Plano</th><th>Status</th><th>Trial</th><th>Próximo vencimento</th></tr></thead>
          <tbody>
            {stores.slice(0, 12).map(s => (
              <tr key={s.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white' }}>
                      {s.name.split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{s.domain}</div>
                    </div>
                  </div>
                </td>
                <td>{s.plan.name}</td>
                <td><StatusBadge status={s.status} /></td>
                <td style={{ color: 'var(--text-secondary)' }}>{s.status === 'trial' ? 'Sim' : 'Não'}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{fmtDate(s.nextDue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {typeof PlanEditorDrawer !== 'undefined' && (
        <PlanEditorDrawer open={drawerOpen} plan={editingPlan} onClose={() => setDrawerOpen(false)} onSave={handleSave} />
      )}
    </div>
  );
};

// ── USUÁRIOS INTERNOS ────────────────────────────────────────────────────────
const UsuariosPage = ({ onToast, internalUsers, setInternalUsers, logAction }) => {
  const [users, setUsers] = useState(() => internalUsers.map(u => ({ ...u, permissions: {} })));
  useEffect(() => { if (setInternalUsers) setInternalUsers(users); }, [users]);

  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [editingUser,   setEditingUser]   = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [confirmToggle, setConfirmToggle] = useState(null);

  const openCreate = () => { setEditingUser(null); setDrawerOpen(true); };
  const openEdit   = (u) => { setEditingUser(u);   setDrawerOpen(true); };

  const handleSave = (saved) => {
    setUsers(prev => {
      const idx = prev.findIndex(u => u.email === saved.email);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...prev[idx], ...saved }; return next; }
      return [...prev, { ...saved, last: 'Nunca' }];
    });
    setDrawerOpen(false);
    if (logAction) logAction({ acao: editingUser ? `Editou usuário ${saved.name}` : `Criou usuário ${saved.name}`, loja: '—', kind: 'info' });
    if (onToast) onToast(editingUser ? `${saved.name} atualizado` : `${saved.name} criado`);
  };

  const doToggleActive = (email) => {
    const u = users.find(x => x.email === email);
    const newStatus = u?.status === 'ativo' ? 'inativo' : 'ativo';
    setUsers(prev => prev.map(x => x.email === email ? { ...x, status: newStatus } : x));
    if (logAction) logAction({ acao: newStatus === 'inativo' ? `Desativou ${u?.name}` : `Reativou ${u?.name}`, loja: '—', kind: newStatus === 'inativo' ? 'danger' : 'info' });
  };

  const filtered    = filterStatus === 'all' ? users : users.filter(u => u.status === filterStatus);
  const activeCount = users.filter(u => u.status === 'ativo').length;

  const RoleBadge = ({ role }) => {
    if (role === 'Super admin') return <span className="badge danger"><span className="dot"></span>{role}</span>;
    if (role === 'Admin')       return <span className="badge info"><span className="dot"></span>{role}</span>;
    return <span className="badge neutral"><span className="dot"></span>{role}</span>;
  };

  return (
    <div className="content" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <div>
          <h1>Usuários da plataforma</h1>
          <p className="sub">Equipe interna da Tezus com acesso ao Super Admin · {activeCount} ativos</p>
        </div>
        <div className="actions">
          <button className="btn primary" onClick={openCreate}><Icon name="plus" size={14} />Criar usuário</button>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="filter-chips">
            {[
              { id: 'all',     label: 'Todos',    count: users.length },
              { id: 'ativo',   label: 'Ativos',   count: users.filter(u => u.status === 'ativo').length },
              { id: 'inativo', label: 'Inativos', count: users.filter(u => u.status === 'inativo').length },
            ].map(f => (
              <button key={f.id} className={'filter-chip ' + (filterStatus === f.id ? 'active' : '')} onClick={() => setFilterStatus(f.id)}>
                {f.label} <span className="count-x">{f.count}</span>
              </button>
            ))}
          </div>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: '26%' }}>Usuário</th>
              <th>E-mail</th>
              <th>Perfil</th>
              <th>Último acesso</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, i) => (
              <tr key={i}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1B3D60', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
                      {u.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}>{u.email}</td>
                <td><RoleBadge role={u.role} /></td>
                <td style={{ color: 'var(--text-secondary)' }}>{u.last}</td>
                <td>
                  {u.status === 'ativo'
                    ? <span className="badge success"><span className="dot"></span>Ativo</span>
                    : <span className="badge neutral"><span className="dot"></span>Inativo</span>}
                </td>
                <td>
                  <div className="row-actions" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="icon-action" title="Editar" onClick={() => openEdit(u)}><Icon name="edit" size={13} /></button>
                    <button className={'icon-action' + (u.status === 'ativo' ? ' danger' : '')} title={u.status === 'ativo' ? 'Desativar acesso' : 'Reativar acesso'} onClick={() => setConfirmToggle(u)}>
                      <Icon name={u.status === 'ativo' ? 'suspend' : 'play'} size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        onConfirm={() => {
          doToggleActive(confirmToggle.email);
          setConfirmToggle(null);
          if (onToast) onToast(confirmToggle.status === 'ativo' ? `${confirmToggle.name} desativado` : `${confirmToggle.name} reativado`);
        }}
        title={confirmToggle?.status === 'ativo' ? `Desativar ${confirmToggle?.name}?` : `Reativar ${confirmToggle?.name}?`}
        body={confirmToggle?.status === 'ativo'
          ? <>O acesso será <strong>revogado imediatamente</strong>. O usuário não conseguirá entrar no Super Admin.</>
          : <>O acesso será <strong>restaurado imediatamente</strong>.</>}
        confirmLabel={confirmToggle?.status === 'ativo' ? 'Desativar acesso' : 'Reativar acesso'}
        danger={confirmToggle?.status === 'ativo'}
      />

      {typeof UserEditorDrawer !== 'undefined' && (
        <UserEditorDrawer open={drawerOpen} user={editingUser} onClose={() => setDrawerOpen(false)} onSave={handleSave} />
      )}
    </div>
  );
};

// ── CONFIGURAÇÕES GLOBAIS DA PLATAFORMA ──────────────────────────────────────
const ConfigPage = ({ onToast, logAction }) => {
  const [form, setForm] = useState({
    moeda: 'BRL', idioma: 'pt-BR',
    smtpHost: 'smtp.sendgrid.net', smtpPort: '587', smtpUser: 'apikey', smtpFrom: 'no-reply@tezus.com',
    limProd: 100, limUsers: 2,
    trialDefaultDays: 14,
    requireMFA: true,
    sessionTimeout: 60,
  });
  const [smtpTesting, setSmtpTesting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const handleSave = () => {
    if (onToast) onToast('Configurações salvas');
    if (logAction) logAction({ acao: 'Alterou configurações globais', loja: '—', kind: 'neutral' });
  };

  const handleTestSmtp = () => {
    setSmtpTesting(true);
    setTimeout(() => {
      setSmtpTesting(false);
      const ok = Math.random() > 0.3;
      if (onToast) onToast(ok ? `Conexão SMTP OK — e-mail de teste enviado para ${form.smtpFrom}` : `Falha na conexão SMTP — verifique host e credenciais`);
    }, 1500);
  };

  const labelStyle = { display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 500 };
  const inputStyle = { width: '100%', height: 36, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  return (
    <div className="content" style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div><h1>Configurações globais</h1><p className="sub">Padrões aplicados a todas as lojas da plataforma.</p></div>
        <div className="actions"><button className="btn primary" onClick={handleSave}>Salvar alterações</button></div>
      </div>
      <div className="stack-md">
        <div className="card">
          <div className="card-head"><h3>Geral</h3></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label style={labelStyle}>Moeda padrão</label>
              <select className="select" style={{ width: '100%' }} value={form.moeda} onChange={set('moeda')}>
                <option value="BRL">Real (R$)</option><option value="USD">Dólar (US$)</option><option value="EUR">Euro (€)</option>
              </select>
            </div>
            <div><label style={labelStyle}>Idioma padrão</label>
              <select className="select" style={{ width: '100%' }} value={form.idioma} onChange={set('idioma')}>
                <option value="pt-BR">Português (Brasil)</option><option value="en">Inglês</option><option value="es">Espanhol</option>
              </select>
            </div>
            <div><label style={labelStyle}>Trial padrão (dias)</label><input style={inputStyle} type="number" value={form.trialDefaultDays} onChange={set('trialDefaultDays')} /></div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>E-mail transacional (SMTP)</h3><span className="sub">Usado para envios de cobrança e notificações</span>
            <div className="actions">
              <button className="btn sm" onClick={handleTestSmtp} disabled={smtpTesting}>
                <Icon name="refresh" size={12} />{smtpTesting ? 'Testando…' : 'Testar conexão SMTP'}
              </button>
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label style={labelStyle}>Servidor</label><input style={inputStyle} value={form.smtpHost} onChange={set('smtpHost')} /></div>
            <div><label style={labelStyle}>Porta</label><input style={inputStyle} value={form.smtpPort} onChange={set('smtpPort')} /></div>
            <div><label style={labelStyle}>Usuário</label><input style={inputStyle} value={form.smtpUser} onChange={set('smtpUser')} /></div>
            <div><label style={labelStyle}>Remetente padrão</label><input style={inputStyle} value={form.smtpFrom} onChange={set('smtpFrom')} /></div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Segurança</h3></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.requireMFA} onChange={set('requireMFA')} style={{ width: 16, height: 16, accentColor: 'var(--brand-navy)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Exigir MFA para Super admins</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Autenticação em duas etapas obrigatória.</div>
              </div>
            </label>
            <div style={{ maxWidth: 260 }}>
              <label style={labelStyle}>Timeout de sessão (minutos)</label>
              <input style={inputStyle} type="number" value={form.sessionTimeout} onChange={set('sessionTimeout')} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Limites padrão</h3><span className="sub">Aplicados a lojas novas sem plano explícito</span></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label style={labelStyle}>Produtos</label><input style={inputStyle} type="number" value={form.limProd} onChange={set('limProd')} /></div>
            <div><label style={labelStyle}>Usuários internos da loja</label><input style={inputStyle} type="number" value={form.limUsers} onChange={set('limUsers')} /></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── AUDITORIA ────────────────────────────────────────────────────────────────
const AuditoriaPage = ({ auditLog = [] }) => {
  const [userFilter,    setUserFilter]    = useState('all');
  const [dateFilter,    setDateFilter]    = useState('7d');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const users = [...new Set(auditLog.map(a => a.user))];

  const getTimestamp = (entry) => {
    if (entry.timestamp instanceof Date) return entry.timestamp;
    const w = entry.when || '';
    const now = makeDate(0);
    if (w.startsWith('Hoje'))   return now;
    if (w.startsWith('Ontem'))  return addDays(now, -1);
    const MONTHS = { 'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5, 'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11 };
    const m = w.match(/^(\d+)\s+([A-Za-zç]+)/);
    if (m) { const mon = MONTHS[m[2]]; if (mon !== undefined) return new Date(2026, mon, parseInt(m[1])); }
    return addDays(now, -60);
  };

  const now = makeDate(0);
  const filtered = auditLog.filter(a => {
    if (userFilter !== 'all' && a.user !== userFilter) return false;
    if (dateFilter === 'all') return true;
    const diffH = (now - getTimestamp(a)) / 3600000;
    if (dateFilter === '24h') return diffH <= 24;
    if (dateFilter === '7d')  return diffH <= 24 * 7;
    if (dateFilter === '30d') return diffH <= 24 * 30;
    return true;
  });

  const handleExport = () => {
    const headers = ['Usuário', 'Ação', 'Loja relacionada', 'Data/hora'];
    const rows = filtered.map(a => [a.user, a.action, a.storeName || '—', a.when]);
    const filename = `auditoria-tezus-${new Date().toISOString().slice(0, 10)}.csv`;
    const escape = v => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a2 = document.createElement('a'); a2.href = url; a2.download = filename; a2.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="content wide" style={{ maxWidth: 1200 }}>
      <div className="page-header">
        <div><h1>Logs e auditoria</h1><p className="sub">Todas as ações da equipe interna são registradas aqui.</p></div>
        <div className="actions"><button className="btn" onClick={handleExport}><Icon name="download" size={14} />Exportar</button></div>
      </div>
      <div className="table-wrap">
        <div className="table-toolbar">
          <select className="select" value={userFilter} onChange={e => setUserFilter(e.target.value)}>
            <option value="all">Todos os usuários</option>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <select className="select" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
            <option value="24h">Últimas 24h</option>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="all">Tudo</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} de {auditLog.length} eventos</span>
        </div>
        <table className="data">
          <thead><tr><th>Usuário</th><th>Ação</th><th>Loja relacionada</th><th>Data/hora</th></tr></thead>
          <tbody>
            {filtered.map((a, i) => (
              <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelectedEvent(a)}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: '#1B3D60', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>{a.user.split(' ').map(n => n[0]).slice(0, 2).join('')}</div>
                  <span style={{ fontWeight: 500 }}>{a.user}</span>
                </div></td>
                <td>{a.kind === 'danger' ? <span className="badge danger"><span className="dot"></span>{a.action}</span> : <span style={{ color: 'var(--text)' }}>{a.action}</span>}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{a.storeName}</td>
                <td style={{ color: 'var(--text-muted)' }}>{a.when}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {typeof AuditDetailDrawer !== 'undefined' && (
        <AuditDetailDrawer open={!!selectedEvent} event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
};

const PlanosLegadoOutOfMvpPage = () => {
  const navigate = useNavigate();
  return (
  <div className="content" style={{ maxWidth: 640, margin: '0 auto' }}>
    <div className="card" style={{ padding: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Icon name="plans" size={22} style={{ color: 'var(--text-muted)' }} />
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Planos &amp; Assinaturas</h1>
        <span className="badge neutral" style={{ marginLeft: 4 }}><span className="dot" />Fora do MVP</span>
      </div>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
        O módulo completo de assinaturas, faturas e gateway não faz parte do MVP operacional.
        Use as rotas abaixo para gerenciar planos, permissões e status comercial com dados reais.
      </p>
      <ul style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 24, paddingLeft: 18 }}>
        <li><strong>Planos &amp; Permissões</strong> — rotas e permissões por plano (Basic/Pro).</li>
        <li><strong>Billing &gt; Status Comercial</strong> — trial, inadimplência, suspensão e cancelamento.</li>
        <li><strong>Detalhe da loja</strong> — alterar plano do tenant e sincronizar permissões.</li>
      </ul>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn primary" onClick={() => navigate(paths.planosPermissoes())}>
          <Icon name="plans" size={14} />Ir para Planos &amp; Permissões
        </button>
        <button type="button" className="btn" onClick={() => navigate(paths.billing())}>
          <Icon name="cardCoin" size={14} />Ir para Billing
        </button>
        <button type="button" className="btn ghost" onClick={() => navigate(paths.lojas())}>
          Voltar para Lojas
        </button>
      </div>
      {typeof saShouldUseMockNotifications === 'function' && !saShouldUseMockNotifications() && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 20, marginBottom: 0 }}>
          Para ver o protótipo legado com dados fictícios, defina <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>USE_MOCK_TENANTS = true</code> em ambiente de desenvolvimento.
        </p>
      )}
    </div>
  </div>
  );
};

const OutOfMvpPage = ({ title }) => {
  const navigate = useNavigate();
  return (
  <div className="content" style={{ maxWidth: 560, margin: '0 auto' }}>
    <div className="card" style={{ padding: 32, textAlign: 'center' }}>
      <Icon name="alert" size={32} style={{ color: 'var(--text-muted)', marginBottom: 16, opacity: 0.5 }} />
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{title || 'Fora do MVP'}</h1>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
        Este módulo ainda não faz parte do Super Admin operacional. Use Dashboard, Lojas, Billing (Status comercial), Auditoria, Equipe ou Repairs.
      </p>
      <button type="button" className="btn primary" onClick={() => navigate(paths.dashboard())}>Ir para o Dashboard</button>
    </div>
  </div>
  );
};

export {
  AuditoriaPage,
  ConfigPage,
  OutOfMvpPage,
  PlanosLegadoOutOfMvpPage,
  PlanosPage,
  UsuariosPage,
};
